// src/pages/PatientTriageResultPage.jsx
// Route: /patient/results/:triageId
//
// Plain-language triage results screen — the core value delivery moment.
// Shows: current health state (GREEN/AMBER/RED/CRITICAL), what it means,
// what to do next, link to Ask Previu chat.
//
// Reads from GET /api/patients/{patientId}/triage-history (existing endpoint).
// Finds the result by triageId from route params.
//
// Gap Report — PatientTriageResultPage.jsx
// ✅ Confirmed: risk state, plain language, next steps, Ask Previu link
// ⚠️ Assumptions: No Figma frame. Follows design tokens + existing patterns.
// ❌ Missing: Figma frames for all risk states. Fallback: color tokens per state.
// 🔗 Existing: PatientLayout, usePatient(), triage-history endpoint
// 🚫 Deviations: None

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import Icon from '../components/Icon';
import { AskPreviuBar } from './PatientTodayPage';
import { buildMarkerHistoryMap } from '../utils/markerHistory';
import {
  RISK_CONFIG, WhatThisMeans, WhatToDoNext,
} from '../components/PatientResultSections';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{
          height: i === 1 ? 140 : 80, borderRadius: 12,
          background: 'var(--bg-subtle)', opacity: 0.6 + i * 0.1,
        }} />
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function PatientTriageResultPage() {
  const { triageId }          = useParams();
  const navigate              = useNavigate();
  const { patient }           = usePatient();
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [markers, setMarkers] = useState([]);
  const [markerCount, setMarkerCount] = useState(null); // total, before the top-8 slice below
  const [markerHistory, setMarkerHistory] = useState({});
  const fetchedRef            = useRef(false);
  const [tab, setTab]         = useState('meaning'); // meaning | next | documents

  // Full clinical report (6-tab TriageReportModal — same component navigators use).
  // Fetched lazily, only when the patient explicitly asks to see it — same
  // "generate on demand, never in bulk" principle StorageService applies to
  // presigned URLs.

  useEffect(() => {
    if (!patient?.id || fetchedRef.current) return;
    fetchedRef.current = true;

    async function fetchResult() {
      try {
        const token = localStorage.getItem('previu_token');
        const res   = await fetch(
          `${API_BASE}/patients/${patient.id}/triage-history`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const history = await res.json();

        // Find the specific result by triageId, or use the most recent
        const requested = triageId
          ? history.find(r => r.id === triageId)
          : history[0];

        if (!requested) throw new Error('Result not found');

        // A Re-run saves a NEW triage row for the same health record and leaves
        // the old one in history. An old link (a bookmark, a notification)
        // would otherwise keep showing the superseded row — including its
        // stale lifestyle plan — so always show the newest row of that record.
        const found = requested.healthRecordId
          ? history
              .filter(r => r.healthRecordId === requested.healthRecordId)
              .reduce((a, b) => (new Date(b.createdAt) > new Date(a.createdAt) ? b : a), requested)
          : requested;
        setResult(found);
        // Same shape buildMarkerHistoryMap expects (drawDate/createdAt +
        // claudeRawResponse) — the full triage-history response already
        // fetched above, so a flagged marker's trend can be shown inline
        // without a second round trip.
        setMarkerHistory(buildMarkerHistoryMap(history));

        // Parse markers from claudeRawResponse — kept in full (not capped)
        // now that the only thing built from it is the flagged-marker key
        // findings below; the complete list lives on the Markers tab.
        if (found.claudeRawResponse) {
          try {
            const raw  = JSON.parse(found.claudeRawResponse);
            const mkrs = raw.markers || raw.sub_scores?.markers || [];
            setMarkerCount(mkrs.length);
            setMarkers(mkrs);
          } catch { /* claudeRawResponse may be null or malformed — safe to ignore */ }
        }
      } catch (e) {
        setError('Could not load your results. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchResult();
  }, [patient?.id, triageId]);

  // ── View full clinical report (6-tab report navigators already see) ─────
  // Reuses TriageReportModal in readOnly mode. Fetches the health record
  // (for fileName/drawDate/recordType shown in the report header) and up to
  // 2 prior results for the trajectory tab — same pattern as
  // HealthRecordsTab.js's handleShowReport.
  const handleViewFullReport = () => {
    if (result?.id) navigate(`/patient/results/${result.id}/report`);
  };

  if (loading) {
    return (
      <PatientAppShell breadcrumbs={[{ label: 'My Results', to: '/patient/results' }, { label: 'Loading…' }]}>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: '24px 0' }}>
          <Skeleton />
        </div>
      </PatientAppShell>
    );
  }

  if (error || !result) {
    return (
      <PatientAppShell breadcrumbs={[{ label: 'My Results' }]}>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: '24px 0' }}>
          <div role="alert" style={{
            padding: '20px', borderRadius: 12, background: 'var(--red-bg)',
            color: 'var(--red)', fontSize: 13, fontFamily: 'var(--font-body)',
          }}>
            {error || 'Result not found.'}
          </div>
        </div>
      </PatientAppShell>
    );
  }

  const riskKey = (result.consensusRiskLevel || 'LOW').toUpperCase();
  const cfg     = RISK_CONFIG[riskKey] || RISK_CONFIG.LOW;
  const drawDate = result.drawDate
    ? new Date(result.drawDate).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : null;

  return (
    <PatientAppShell
      breadcrumbs={[
        { label: 'My Results', to: '/patient/results' },
        { label: drawDate || 'Latest results' },
      ]}
    >
      <div style={{ maxWidth: 620, margin: '0 auto', padding: '24px 0 60px' }}>

        {/* Risk state hero card */}
        <div style={{
          borderRadius: 16, padding: '28px 24px', marginBottom: 20,
          background: cfg.bg,
          border: `2px solid ${cfg.color}`,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>{cfg.icon}</div>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: cfg.color,
            fontFamily: 'var(--font-body)', marginBottom: 6,
          }}>
            {cfg.badge}
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 22,
            color: 'var(--text-primary)', margin: '0 0 10px', lineHeight: 1.3,
          }}>
            {cfg.headline}
          </h1>
          {drawDate && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
              Report date: {drawDate}
            </div>
          )}
        </div>

        {/* Follow-up scope notice — this report didn't retest everything.
            reportScope/riskFloorApplied come straight through from
            /triage-history (V75, HealthRecordService.toTriageResponse) —
            the backend has always computed these; nothing here recomputes
            anything, it just surfaces what was already being returned and
            ignored. Placed before "What this means" so the caveat is read
            before any reassuring risk-level copy. */}
        {result.reportScope === 'FOLLOW_UP' && (
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            background: 'var(--blue-bg)', border: '1.5px solid var(--blue)',
            marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: 18 }}>ℹ️</span>
            <div>
              <div style={{
                fontWeight: 700, fontSize: 13, color: 'var(--blue)',
                fontFamily: 'var(--font-body)', marginBottom: 4,
              }}>
                This was a follow-up test, not a full panel
              </div>
              <p style={{
                fontSize: 13, color: 'var(--text-secondary)', margin: 0,
                fontFamily: 'var(--font-body)', lineHeight: 1.6,
              }}>
                {markerCount
                  ? `This report covers ${markerCount} marker${markerCount === 1 ? '' : 's'}`
                  : 'This report covers a small set of markers'} — it doesn't
                re-check everything. Markers not in this report keep their last known
                value rather than resetting to "unknown".{' '}
                <button
                  type="button"
                  onClick={() => navigate('/patient/markers')}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    color: 'var(--blue)', fontWeight: 700, cursor: 'pointer',
                    textDecoration: 'underline', fontSize: 13, fontFamily: 'var(--font-body)',
                  }}
                >
                  View your full current picture →
                </button>
              </p>
            </div>
          </div>
        )}

        {result.riskFloorApplied && (
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            background: 'var(--amber-bg)', border: '1.5px solid var(--amber)',
            marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <Icon name="alert" size={18} />
            <p style={{
              fontSize: 13, color: 'var(--text-secondary)', margin: 0,
              fontFamily: 'var(--font-body)', lineHeight: 1.6,
            }}>
              The risk level above reflects a higher reading carried over from an earlier
              report — this recheck alone didn't show enough to lower it. We never reduce
              your risk level based on a partial recheck by itself.
            </p>
          </div>
        )}

        {/* Emergency notice stays above the tabs so it can never be missed. */}
        {cfg.urgent && (
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            background: 'var(--red-bg)', border: '1.5px solid var(--red)',
            marginBottom: 16,
          }}>
            <div style={{
              fontWeight: 700, fontSize: 13, color: 'var(--red)',
              fontFamily: 'var(--font-body)', marginBottom: 4,
            }}>
              If you feel unwell right now:
            </div>
            <p style={{
              fontSize: 13, color: 'var(--red)', margin: 0,
              fontFamily: 'var(--font-body)', lineHeight: 1.6,
            }}>
              Call <strong>112</strong> (India emergency) or go to your nearest emergency department.
              Do not wait for a callback if your symptoms feel serious.
            </p>
          </div>
        )}

        {/* Tabs — keeps "what this means", "what to do" and the source files
            one tap apart instead of one long scroll. */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div className="tabs-bar" role="tablist" aria-label="Result sections" style={{ marginBottom: 16 }}>
            {[
              { id: 'meaning', label: 'What this means' },
              { id: 'next', label: 'What to do next' },
              ...(result.healthRecordId ? [{ id: 'documents', label: 'Documents' }] : []),
            ].map(t => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={`tab-btn${tab === t.id ? ' active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div role="tabpanel">
          {tab === 'meaning' && (
            <WhatThisMeans result={result} markers={markers} markerHistory={markerHistory}
              allMarkersTo="/patient/markers" initialVisible={2} />
          )}
          {tab === 'next' && <WhatToDoNext result={result} />}
          {tab === 'documents' && result.healthRecordId && (
            <section style={{
              background: 'var(--bg-surface)', borderRadius: 12, padding: '20px',
              marginBottom: 16, border: '1px solid var(--border)',
            }}>
              <h2 style={{
                fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700,
                color: 'var(--text-primary)', margin: '0 0 4px',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>
                Your original documents
              </h2>
              <p style={{
                fontSize: 13, color: 'var(--text-secondary)',
                fontFamily: 'var(--font-body)', lineHeight: 1.6, margin: '0 0 14px',
              }}>
                This page shows your results in plain language. You can also see the
                complete clinical report.
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {/* --text-primary, not --brand-dark: --brand-dark goes
                    near-black in dark theme, same as --bg-surface, which left
                    this label invisible. Matches the sibling buttons below. */}
                <button
                  type="button"
                  onClick={handleViewFullReport}
                  style={{
                    flex: 1, border: '1.5px solid var(--border)',
                    borderRadius: 10, padding: '11px 16px',
                    fontSize: 13, fontWeight: 600,
                    cursor: 'pointer',
                    color: 'var(--text-primary)', background: 'var(--bg-subtle)',
                    fontFamily: 'var(--font-body)', minWidth: 180,
                  }}
                >
                  📄 View full clinical report
                </button>
              </div>
            </section>
          )}
        </div>

        {/* Same Ask Previu chat bar as the Today screen — a typed question
            lands in the chat already sent. */}
        <div style={{ marginBottom: 16 }}>
          <AskPreviuBar placeholder="Ask about your results…" />
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => navigate('/patient/timeline')}
            style={{
              flex: 1, border: '1.5px solid var(--border)',
              borderRadius: 10, padding: '11px 16px',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              color: 'var(--text-primary)', background: 'var(--bg-surface)',
              fontFamily: 'var(--font-body)', minWidth: 140,
            }}
          >
            📈 View health timeline
          </button>
          <button
            type="button"
            onClick={() => navigate('/patient/care-plan')}
            style={{
              flex: 1, border: '1.5px solid var(--border)',
              borderRadius: 10, padding: '11px 16px',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              color: 'var(--text-primary)', background: 'var(--bg-surface)',
              fontFamily: 'var(--font-body)', minWidth: 140,
            }}
          >
            📋 My care plan
          </button>
        </div>
      </div>
    </PatientAppShell>
  );
}
